/**
 * Phone number helpers.
 *
 * Shops store client phones however staff typed them ("612 34 56 78",
 * "(555) 123-4567", "+34612345678"). Anything that leaves the app — SMS,
 * WhatsApp links — needs E.164. The shop's country supplies the calling
 * code when the number was entered without one.
 */

/** ITU calling codes for the countries ShopLedger supports (see countries.ts). */
export const CALLING_CODES: Record<string, string> = {
  US: "1",
  ES: "34",
};

/**
 * Normalize a phone number to E.164 ("+34612345678").
 * Returns null when the input can't plausibly be a phone number.
 */
export function toE164(raw: string | null | undefined, countryCode: string | null | undefined): string | null {
  if (!raw) return null;
  const trimmed = raw.trim();
  if (!trimmed) return null;

  const hasPlus = trimmed.startsWith("+");
  // "00" international prefix (common in Spain/Europe): 0034 612 ...
  const intlPrefix = trimmed.startsWith("00");
  const digits = trimmed.replace(/\D/g, "");
  if (digits.length < 6) return null;

  if (hasPlus) return `+${digits}`;
  if (intlPrefix) return `+${digits.slice(2)}`;

  const cc = CALLING_CODES[(countryCode || "US").toUpperCase()] ?? "1";

  // Already carries the country's calling code (e.g. "34612345678", "15551234567")
  if (digits.startsWith(cc) && digits.length > nationalLength(cc)) {
    return `+${digits}`;
  }

  // Strip a national trunk prefix where one exists (not used in ES/US, but harmless)
  const national = digits.replace(/^0+/, "");
  return `+${cc}${national}`;
}

/** Typical national-significant-number length per calling code. */
function nationalLength(cc: string): number {
  switch (cc) {
    case "1":
      return 10;
    case "34":
      return 9;
    default:
      return 8;
  }
}

/** Digits only, no "+", as wa.me expects. */
export function toWhatsAppDigits(raw: string | null | undefined, countryCode: string | null | undefined): string | null {
  const e164 = toE164(raw, countryCode);
  return e164 ? e164.slice(1) : null;
}

/** Pretty-print for display: "+34 612 34 56 78" / "+1 (555) 123-4567". */
export function formatPhoneDisplay(raw: string | null | undefined, countryCode: string | null | undefined): string {
  const e164 = toE164(raw, countryCode);
  if (!e164) return raw ?? "";
  if (e164.startsWith("+34") && e164.length === 12) {
    const n = e164.slice(3);
    return `+34 ${n.slice(0, 3)} ${n.slice(3, 5)} ${n.slice(5, 7)} ${n.slice(7)}`;
  }
  if (e164.startsWith("+1") && e164.length === 12) {
    const n = e164.slice(2);
    return `+1 (${n.slice(0, 3)}) ${n.slice(3, 6)}-${n.slice(6)}`;
  }
  return e164;
}
